import {
  Button,
  Tooltip,
  TooltipProps,
  Typography,
  TypographyProps,
  tooltipClasses,
} from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/system';

const FlexBox = styled(Box)({
  display: 'flex',
});

const CenteredFlexBox = styled(FlexBox)({
  justifyContent: 'center',
});

const FullSizeCenteredFlexBox = styled(CenteredFlexBox)({
  width: '100%',
  height: '100%',
});

const CustomBox = styled(Box)(({ theme }) => ({
  padding: '1.375rem 1.125rem 1.3125rem 1.3125rem',
  border: `2px solid ${theme.palette.grey[300]}`,
  borderRadius: 10,
}));

const FormTitle = styled(Typography)(() => ({
  color: '#504E5A',
  fontFamily: '"Neue Frutiger World"',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '150%',
  display: 'flex',
  height: '28px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignSelf: 'stretch',
  marginBottom: '1rem',
}));

const FormBox = styled(Box)(({ theme }) => ({
  padding: '2rem',
  borderRadius: '0.875rem',
  marginBottom: '2rem',
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.15)',
}));

const FormLabel = styled(({ children, ...props }: TypographyProps) => (
  <Typography {...props}>{children}</Typography>
))(({ theme }) => ({
  margin: 0,
  marginBottom: '0.5rem',
  color: theme.palette.grey[950],
  fontSize: '0.8rem',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '140%',
  letterSpacing: '0.015rem',
  textTransform: 'capitalize',
}));

const FormError = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '0.6rem',
  lineHeight: '1rem',
}));

const FormRow = styled(FlexBox)(() => ({
  gap: '1.25rem',
  alignItems: 'flex-end',
  position: 'relative',
}));

const HtmlTooltip = styled(
  ({ className, children, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }}>
      {children}
    </Tooltip>
  ),
  {
    shouldForwardProp: (prop) => !['backgroundColor', 'color'].includes(prop as string),
  },
)<{ backgroundColor?: string; color?: string }>(({ backgroundColor, color, theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: backgroundColor || 'white',
    maxWidth: '20rem',
    fontSize: '0.65rem',
    border: '1px solid #dadde9',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '1rem 1.25rem',
    gap: '0.5rem',

    '& p': {
      color: color || theme.palette.text.main,
    },
  },
}));

const RemovePageContainer = styled(Box)(() => ({
  background: '#F7F8FA',
  borderRadius: '0.625rem',
  padding: '5rem 1.5rem 1.5rem 1.5rem',
}));

const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => !['successButton', 'deleteButton', 'color'].includes(prop as string),
})<{ successButton?: boolean | undefined; deleteButton?: boolean | undefined; color?: string }>(
  ({ successButton, deleteButton, theme, color }) => ({
    minWidth: '1.375rem',
    height: '1.375rem',
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: '0.25rem',
    backgroundColor: successButton
      ? theme.palette.success.light
      : deleteButton
        ? theme.palette.error.light
        : color
          ? theme.palette?.[color].main
          : theme.palette.secondary.light,
    padding: '0 0.5rem',
    color: successButton
      ? theme.palette.success.main
      : deleteButton
        ? theme.palette.error.main
        : color
          ? theme.palette?.[color].contrastText || 'white'
          : theme.palette.secondary.dark,
    textTransform: 'none',

    '& svg': {
      width: '0.75rem',
      height: '0.75rem',
      color: successButton
        ? '#62CB99'
        : deleteButton
          ? '#F3786F'
          : color
            ? theme.palette?.[color].contrastText || 'white'
            : theme.palette.secondary.dark,
    },
  }),
);

const GeneratikTableContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
}));

export {
  FlexBox,
  CenteredFlexBox,
  FullSizeCenteredFlexBox,
  CustomBox,
  FormTitle,
  FormBox,
  FormLabel,
  FormError,
  FormRow,
  HtmlTooltip,
  RemovePageContainer,
  ActionButton,
  GeneratikTableContainer,
};
